import type { Principal, Role } from '../../controlled/commonTypes.tsx';
import type { PrincipalsMap } from '../principalsMap.ts';

import type { useUpdateContentDirectRestrictionsMutation$variables as MutationVariables } from './__generated__/useUpdateContentDirectRestrictionsMutation.graphql.ts';

export type ConfluenceDirectRestrictionPrincipalType = 'USER' | 'GROUP';
export type ConfluenceDirectRestrictionPermission = 'view' | 'edit';

type PermissionOrNone = ConfluenceDirectRestrictionPermission | null;

function mapPrincipalTypeToDirectRestrictionPrincipalType(
	principalType: Principal['type'],
): ConfluenceDirectRestrictionPrincipalType {
	switch (principalType) {
		case 'group':
		case 'team':
			return 'GROUP';
		case 'user':
		case 'external_user':
		default:
			return 'USER';
	}
}

function roleToPermission(role: Role): PermissionOrNone {
	switch (role) {
		case 'can-view':
			return 'view';
		case 'can-edit':
			return 'edit';
		case 'no-access':
			return null;
		default:
			return null;
	}
}

export function buildUpdateContentDirectRestrictionsBulkRemoveVariables({
	cloudId,
	contentId,
	includeInvites,
	principals,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principals: Principal[];
}): MutationVariables {
	const restrictions = principals.map((principal) => {
		const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
		return { principalId: principal.id, principalType } as const;
	});

	return {
		cloudId,
		input: {
			contentId,
			includeInvites,
			remove: {
				view: { restrictions },
				edit: { restrictions },
			},
		},
	};
}

export function buildUpdateContentDirectRestrictionsRemoveVariables({
	cloudId,
	contentId,
	includeInvites,
	principal,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principal: Principal;
}): MutationVariables {
	const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
	const restriction = {
		principalId: principal.id,
		principalType,
	} as const;

	return {
		cloudId,
		input: {
			contentId,
			includeInvites,
			remove: {
				view: { restrictions: [restriction] },
				edit: { restrictions: [restriction] },
			},
		},
	};
}

// Transform a single principal's role change into add/remove buckets for the direct restrictions mutation
export function buildUpdateContentDirectRestrictionsVariables({
	cloudId,
	contentId,
	includeInvites,
	principal,
	previousRole,
	nextRole,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principal: Principal;
	previousRole: Role;
	nextRole: Role;
}): MutationVariables {
	const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
	const previousPermission = roleToPermission(previousRole);
	const nextPermission = roleToPermission(nextRole);

	const restriction = {
		principalId: principal.id,
		principalType,
	} as const;

	const add: NonNullable<MutationVariables['input']['add']> = {};
	const remove: NonNullable<MutationVariables['input']['remove']> = {};

	if (previousPermission) {
		remove[previousPermission] = { restrictions: [restriction] };
	}

	if (nextPermission) {
		add[nextPermission] = { restrictions: [restriction] };
	}

	const input: MutationVariables['input'] = {
		contentId,
		includeInvites,
		...(Object.keys(add).length ? { add } : {}),
		...(Object.keys(remove).length ? { remove } : {}),
	};

	return {
		cloudId,
		input,
	};
}

// Transform group of restrictions into add/remove buckets, bucketing by role -> permission, then into 'add' or 'remove'
export function buildUpdateContentDirectRestrictionsFromStagedPrincipal({
	cloudId,
	contentId,
	includeInvites,
	principalsMap,
	action,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principalsMap: PrincipalsMap;
	action: 'add' | 'remove';
}): MutationVariables {
	const viewRestrictions: Array<{
		principalId: string;
		principalType: ConfluenceDirectRestrictionPrincipalType;
	}> = [];
	const editRestrictions: Array<{
		principalId: string;
		principalType: ConfluenceDirectRestrictionPrincipalType;
	}> = [];

	for (const [principalId, entry] of principalsMap.entries()) {
		const { principal, role } = entry;
		const permission = roleToPermission(role);
		if (!permission) continue;
		const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
		const restriction = { principalId, principalType } as const;
		if (permission === 'view') {
			viewRestrictions.push(restriction);
		} else if (permission === 'edit') {
			editRestrictions.push(restriction);
		}
	}

	const add: NonNullable<MutationVariables['input']['add']> | undefined =
		action === 'add'
			? {
					...(viewRestrictions.length ? { view: { restrictions: viewRestrictions } } : {}),
					...(editRestrictions.length ? { edit: { restrictions: editRestrictions } } : {}),
				}
			: undefined;

	const remove: NonNullable<MutationVariables['input']['remove']> | undefined =
		action === 'remove'
			? {
					...(viewRestrictions.length ? { view: { restrictions: viewRestrictions } } : {}),
					...(editRestrictions.length ? { edit: { restrictions: editRestrictions } } : {}),
				}
			: undefined;

	return {
		cloudId,
		input: {
			contentId,
			includeInvites,
			...(add ? { add } : {}),
			...(remove ? { remove } : {}),
		},
	};
}
